import { logger } from '../utils/logger';
/**
 * RepConnect - NPI Registry API Integration
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

// NPI Registry API endpoints
const NPI_INDIVIDUALS_API = 'https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search';
const NPI_ORGANIZATIONS_API = 'https://clinicaltables.nlm.nih.gov/api/npi_org/v3/search';

// Common medical specialties mapping for better search
const SPECIALTY_MAPPINGS = {
  'cardiology': ['Cardiology', 'Interventional Cardiology', 'Cardiovascular Disease'],
  'orthopedics': ['Orthopedic Surgery', 'Orthopaedic Surgery'],
  'neurology': ['Neurology', 'Neurological Surgery', 'Neurosurgery'],
  'pediatrics': ['Pediatrics', 'Pediatric'],
  'dermatology': ['Dermatology'],
  'psychiatry': ['Psychiatry', 'Child & Adolescent Psychiatry'],
  'family': ['Family Practice', 'Family Medicine'],
  'internal': ['Internal Medicine'],
  'emergency': ['Emergency Medicine'],
  'radiology': ['Radiology', 'Diagnostic Radiology'],
  'anesthesiology': ['Anesthesiology'],
  'pathology': ['Pathology'],
  'surgery': ['Surgery', 'General Surgery'],
  'oncology': ['Oncology', 'Medical Oncology', 'Hematology-Oncology', 'Radiation Oncology'],
  'gastroenterology': ['Gastroenterology'],
  'pulmonology': ['Pulmonary Disease', 'Pulmonology'],
  'endocrinology': ['Diabetes', 'Endocrinology', 'Endocrinology, Diabetes & Metabolism'],
  'rheumatology': ['Rheumatology'],
  'nephrology': ['Nephrology'],
  'urology': ['Urology'],
  'obstetrics': ['Obstetrics/Gynecology', 'Obstetrics & Gynecology', 'Gynecology'],
  'ophthalmology': ['Ophthalmology'],
  'otolaryngology': ['Otolaryngology', 'ENT'],
  'plastic': ['Plastic Surgery', 'Plastic and Reconstructive Surgery'],
  'infectious': ['Infectious Disease']
};

export const NPIAPI = {
  /**
   * Search for individual healthcare providers using NPI Registry with separated fields
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.name - Provider name (first or last name)
   * @param {string} searchParams.location - Location (city, state, ZIP code)
   * @param {string} searchParams.specialty - Medical specialty filter
   * @param {number} searchParams.limit - Number of results (max 500)
   * @param {number} searchParams.offset - Starting position for pagination
   * @returns {Promise<Object>} Formatted search results
   */
  async searchProviders(searchParams = {}) {
    try {
      const { name = '', location = '', specialty = 'all', limit = 20, offset = 0 } = searchParams;
      
      // Build search terms prioritizing the most specific field
      let searchTerms = [];
      let locationFilter = null;
      
      // Parse location input for post-filtering
      if (location.trim()) {
        const locationInput = location.trim();
        
        // ZIP code
        if (/^\d{5}(-\d{4})?$/.test(locationInput)) {
          locationFilter = { type: 'zip', value: locationInput };
        }
        // State abbreviation  
        else if (locationInput.length === 2 && /^[A-Z]{2}$/i.test(locationInput)) {
          locationFilter = { type: 'state', value: locationInput.toUpperCase() };
        }
        // Full state names
        else if (['iowa', 'texas', 'california', 'florida', 'nevada', 'colorado', 'oregon', 'washington', 'arizona'].includes(locationInput.toLowerCase())) {
          const stateMapping = {
            'iowa': 'IA', 'texas': 'TX', 'california': 'CA', 'florida': 'FL',
            'nevada': 'NV', 'colorado': 'CO', 'oregon': 'OR', 'washington': 'WA',
            'arizona': 'AZ'
          };
          locationFilter = { type: 'state', value: stateMapping[locationInput.toLowerCase()] };
        }
        // City names
        else {
          locationFilter = { type: 'city', value: locationInput.toLowerCase() };
        }
      }
      
      // Priority 1: If we have a name, search by name + specialty
      if (name.trim()) {
        searchTerms.push(name.trim());
        if (specialty && specialty !== 'all') {
          const specialtyTerms = SPECIALTY_MAPPINGS[specialty.toLowerCase()] || [specialty];
          searchTerms.push(specialtyTerms[0]);
        }
      }
      // Priority 2: If we have a location but no name, search by location + specialty  
      else if (locationFilter) {
        if (locationFilter.type === 'zip' || locationFilter.type === 'state') {
          searchTerms.push(locationFilter.value);
        } else {
          // For cities, we'll do a broader search and filter after
          if (specialty && specialty !== 'all') {
            const specialtyTerms = SPECIALTY_MAPPINGS[specialty.toLowerCase()] || [specialty];
            searchTerms.push(specialtyTerms[0]);
          }
        }
      }
      // Priority 3: If we only have specialty, search by specialty
      else if (specialty && specialty !== 'all') {
        const specialtyTerms = SPECIALTY_MAPPINGS[specialty.toLowerCase()] || [specialty];
        searchTerms.push(specialtyTerms[0]);
      }
      
      const termsString = searchTerms.join(' ');
      
      logger.debug('🔍 Simplified Search:', {
        name: name,
        location: location,
        specialty: specialty,
        locationFilter: locationFilter,
        searchTerms: searchTerms,
        finalSearchString: termsString
      });
      
      if (!termsString) {
        return { providers: [], total: 0, hasMore: false };
      }
      
      // Make API request
      const url = new URL(NPI_INDIVIDUALS_API);
      url.searchParams.append('terms', termsString);
      url.searchParams.append('maxList', Math.min(limit, 500).toString());
      url.searchParams.append('offset', offset.toString());
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`NPI API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Process and filter results
      return this.processSearchResults(data, offset, limit, locationFilter);
    } catch (error) {
      logger.error('Error searching NPI providers:', error);
      throw new Error(`Provider search failed: ${error.message}`);
    }
  },

  /**
   * Process search results from NPI API response
   * @param {Array} data - NPI API response data
   * @param {number} offset - Offset for pagination
   * @param {number} limit - Limit for pagination
   * @param {Object} locationFilter - Location filter for post-processing
   * @returns {Object} Processed search results
   */
  processSearchResults(data, offset, limit, locationFilter = null) {
    const [total, npiNumbers, , providerDetails] = data;
    
    if (!Array.isArray(providerDetails)) {
      return { providers: [], total: 0, hasMore: false };
    }
    
    // Transform to our provider format
    let providers = providerDetails.map((details, index) => {
      const [fullName, npi, providerType, address] = details;
      
      return this.transformNPIProvider({
        fullName,
        npi,
        providerType,
        address,
        originalData: details
      });
    });
    
    // Apply location filtering if specified
    if (locationFilter) {
      providers = providers.filter(provider => {
        const address = provider.address.toLowerCase();
        
        switch (locationFilter.type) {
          case 'zip':
            return provider.zipCode === locationFilter.value || 
                   address.includes(locationFilter.value);
          case 'state':
            return provider.state === locationFilter.value ||
                   address.includes(locationFilter.value.toLowerCase());
          case 'city':
            return provider.city.toLowerCase().includes(locationFilter.value) ||
                   address.includes(locationFilter.value);
          default:
            return true;
        }
      });
    }
    
    return {
      providers,
      total: locationFilter ? providers.length : (total || 0),
      hasMore: locationFilter ? false : (offset + providers.length) < (total || 0),
      offset,
      limit
    };
  },

  /**
   * Transform NPI provider data to our internal format
   * @param {Object} npiData - Raw NPI provider data
   * @returns {Object} Transformed provider object
   */
  transformNPIProvider(npiData) {
    const { fullName, npi, providerType, address, originalData } = npiData;
    
    // Parse name (usually "LAST, FIRST MIDDLE")
    const nameParts = fullName.split(',').map(part => part.trim());
    const lastName = nameParts[0] || '';
    const firstMiddle = nameParts[1] || '';
    const displayName = firstMiddle ? `Dr. ${firstMiddle} ${lastName}` : `Dr. ${lastName}`;
    
    // Extract specialty from provider type
    const specialty = this.extractSpecialty(providerType);
    
    // Parse address
    const addressParts = address.split(',').map(part => part.trim());
    const street = addressParts[0] || '';
    const cityState = addressParts.slice(1).join(', ');
    
    // Extract city and state
    const cityStateMatch = cityState.match(/^(.+),\s*([A-Z]{2})\s+(\d{5}(-\d{4})?)$/);
    const city = cityStateMatch ? cityStateMatch[1] : cityState;
    const state = cityStateMatch ? cityStateMatch[2] : '';
    const zipCode = cityStateMatch ? cityStateMatch[3] : '';
    
    return {
      id: npi, // Use NPI as unique identifier
      name: displayName,
      specialty: specialty,
      hospital: this.extractHospitalOrPractice(address, providerType),
      rating: this.generatePlaceholderRating(),
      meetingsCompleted: 0,
      preferredTime: 'Not specified',
      address: address,
      street: street,
      city: city,
      state: state,
      zipCode: zipCode,
      phone: 'Contact for phone',
      email: 'Contact for email',
      npi: npi,
      providerType: providerType,
      source: 'NPI Registry',
      lastUpdated: new Date().toISOString(),
      originalData: originalData,
      // Add missing fields for compatibility
      favoriteRestaurants: null,
      dietaryRestrictions: null,
      lastMeeting: null,
      averageOrderValue: null
    };
  },

  /**
   * Extract medical specialty from provider type string
   * @param {string} providerType - NPI provider type
   * @returns {string} Cleaned specialty
   */
  extractSpecialty(providerType) {
    if (!providerType) return 'General Medicine';
    
    // Remove "Physician/" prefix if present
    const specialty = providerType.replace(/^Physician\//, '');
    
    // Handle common specialty variations
    const specialtyMappings = {
      'Internal Medicine': 'Internal Medicine',
      'Family Medicine': 'Family Medicine',
      'Family Practice': 'Family Medicine',
      'Cardiovascular Disease': 'Cardiology',
      'Interventional Cardiology': 'Cardiology',
      'Orthopedic Surgery': 'Orthopedics',
      'Orthopaedic Surgery': 'Orthopedics',
      'Neurological Surgery': 'Neurology',
      'Child & Adolescent Psychiatry': 'Psychiatry',
      'Emergency Medicine': 'Emergency Medicine',
      'Diagnostic Radiology': 'Radiology'
    };
    
    return specialtyMappings[specialty] || specialty || 'General Medicine';
  },

  /**
   * Extract hospital or practice name from address/provider type
   * @param {string} address - Provider address
   * @param {string} providerType - Provider type
   * @returns {string} Hospital or practice name
   */
  extractHospitalOrPractice(address, providerType) {
    // This is a simplified extraction - in real implementation,
    // you might want to cross-reference with hospital databases
    
    // Check if address contains common hospital indicators
    const hospitalKeywords = ['HOSPITAL', 'MEDICAL CENTER', 'HEALTH SYSTEM', 'CLINIC'];
    const addressUpper = address.toUpperCase();
    
    for (const keyword of hospitalKeywords) {
      if (addressUpper.includes(keyword)) {
        // Try to extract the hospital name from the address
        const parts = address.split(',')[0];
        if (parts.length > 20) { // Likely contains hospital name
          return parts.trim();
        }
      }
    }
    
    // Default to generic practice name
    const specialty = this.extractSpecialty(providerType);
    return `${specialty} Practice`;
  },

  /**
   * Generate a placeholder rating for NPI providers
   * @returns {number} Rating between 4.0 and 5.0
   */
  generatePlaceholderRating() {
    // Generate consistent but varied ratings between 4.0 and 5.0
    return parseFloat((4.0 + Math.random() * 1.0).toFixed(1));
  },

  /**
   * Search for providers by specific NPI number
   * @param {string} npi - NPI number
   * @returns {Promise<Object|null>} Provider details or null
   */
  async getProviderByNPI(npi) {
    try {
      const url = new URL(NPI_INDIVIDUALS_API);
      url.searchParams.append('terms', npi);
      url.searchParams.append('maxList', '1');
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`NPI API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      const [total, npiNumbers, , providerDetails] = data;
      
      if (!providerDetails || providerDetails.length === 0) {
        return null;
      }
      
      const [fullName, npiNumber, providerType, address] = providerDetails[0];
      
      return this.transformNPIProvider({
        fullName,
        npi: npiNumber,
        providerType,
        address,
        originalData: providerDetails[0]
      });
      
    } catch (error) {
      logger.error('Error fetching provider by NPI:', error);
      return null;
    }
  },

  /**
   * Parse a unified search query and extract components
   * @param {string} query - The full search query
   * @returns {Object} Parsed components { names: [], locations: [], specialties: [], other: [] }
   */
  parseUnifiedQuery(query) {
    if (!query || !query.trim()) {
      return { names: [], locations: [], specialties: [], other: [] };
    }

    const tokens = query.toLowerCase().split(/\s+/).filter(token => token.length > 0);
    const parsed = { names: [], locations: [], specialties: [], other: [] };

    // Enhanced location patterns
    const locationPatterns = {
      // State abbreviations
      stateAbbrevs: ['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 
                     'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 
                     'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 
                     'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy'],
      
      // Major cities that should be treated as locations
      majorCities: ['los', 'angeles', 'new', 'york', 'chicago', 'houston', 'phoenix', 'philadelphia', 
                    'san', 'antonio', 'diego', 'dallas', 'jose', 'austin', 'jacksonville', 'fort', 
                    'worth', 'columbus', 'charlotte', 'francisco', 'indianapolis', 'seattle', 
                    'denver', 'washington', 'boston', 'nashville', 'baltimore', 'oklahoma', 
                    'portland', 'vegas', 'milwaukee', 'albuquerque', 'tucson', 'fresno', 'sacramento', 
                    'mesa', 'kansas', 'atlanta', 'omaha', 'raleigh', 'miami', 'cleveland', 'tulsa', 
                    'oakland', 'minneapolis', 'wichita', 'arlington', 'tampa', 'new orleans', 'honolulu'],
      
      // Common location keywords
      locationKeywords: ['city', 'county', 'beach', 'springs', 'hills', 'valley', 'park', 'heights', 
                        'falls', 'lake', 'river', 'bay', 'island', 'mountain', 'grove', 'field']
    };

    // Specialty keywords
    const specialtyKeywords = {
      'cardiology': ['cardiology', 'cardiologist', 'heart', 'cardiac'],
      'orthopedics': ['orthopedic', 'orthopedics', 'orthopaedic', 'bone', 'joint'],
      'neurology': ['neurology', 'neurologist', 'brain', 'neurological', 'neuro'],
      'pediatrics': ['pediatric', 'pediatrics', 'pediatrician', 'children', 'child'],
      'dermatology': ['dermatology', 'dermatologist', 'skin'],
      'psychiatry': ['psychiatry', 'psychiatrist', 'mental', 'behavioral'],
      'family': ['family', 'primary', 'general'],
      'internal': ['internal', 'internist'],
      'emergency': ['emergency', 'er', 'trauma'],
      'radiology': ['radiology', 'radiologist', 'imaging'],
      'anesthesiology': ['anesthesia', 'anesthesiology', 'anesthesiologist'],
      'pathology': ['pathology', 'pathologist'],
      'surgery': ['surgery', 'surgeon', 'surgical'],
      'oncology': ['oncology', 'oncologist', 'cancer'],
      'gastroenterology': ['gastroenterology', 'gastroenterologist', 'gastro', 'digestive'],
      'pulmonology': ['pulmonology', 'pulmonologist', 'lung', 'respiratory'],
      'endocrinology': ['endocrinology', 'endocrinologist', 'diabetes', 'hormone'],
      'rheumatology': ['rheumatology', 'rheumatologist', 'arthritis'],
      'nephrology': ['nephrology', 'nephrologist', 'kidney'],
      'urology': ['urology', 'urologist', 'urinary'],
      'obstetrics': ['obstetrics', 'gynecology', 'obgyn', 'womens'],
      'ophthalmology': ['ophthalmology', 'ophthalmologist', 'eye', 'vision'],
      'otolaryngology': ['ent', 'otolaryngology', 'ear', 'nose', 'throat'],
      'plastic': ['plastic', 'cosmetic', 'reconstructive'],
      'infectious': ['infectious', 'disease', 'infection']
    };

    // Process each token
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const nextToken = tokens[i + 1];
      let processed = false;

      // Check for ZIP codes
      if (/^\d{5}(-\d{4})?$/.test(token)) {
        parsed.locations.push(token);
        processed = true;
      }
      
      // Check for state abbreviations (including full state names)
      else if (locationPatterns.stateAbbrevs.includes(token) || 
               ['iowa', 'texas', 'california', 'florida', 'nevada', 'colorado', 'oregon', 'washington', 'arizona'].includes(token)) {
        // Convert full state names to abbreviations
        const stateMapping = {
          'iowa': 'IA', 'texas': 'TX', 'california': 'CA', 'florida': 'FL',
          'nevada': 'NV', 'colorado': 'CO', 'oregon': 'OR', 'washington': 'WA',
          'arizona': 'AZ', 'illinois': 'IL', 'michigan': 'MI', 'ohio': 'OH'
        };
        parsed.locations.push(stateMapping[token] || token.toUpperCase());
        processed = true;
      }
      
      // Check for two-word cities (like "Los Angeles", "New York", "San Antonio")
      else if (nextToken && 
               ((token === 'los' && nextToken === 'angeles') ||
                (token === 'new' && nextToken === 'york') ||
                (token === 'san' && (nextToken === 'antonio' || nextToken === 'diego' || nextToken === 'francisco' || nextToken === 'jose')) ||
                (token === 'fort' && nextToken === 'worth') ||
                (token === 'kansas' && nextToken === 'city') ||
                (token === 'oklahoma' && nextToken === 'city') ||
                (token === 'las' && nextToken === 'vegas') ||
                (token === 'new' && nextToken === 'orleans'))) {
        parsed.locations.push(`${token} ${nextToken}`);
        i++; // Skip next token as it's part of this location
        processed = true;
      }
      
      // Check for single-word major cities
      else if (locationPatterns.majorCities.includes(token) || locationPatterns.locationKeywords.includes(token)) {
        parsed.locations.push(token);
        processed = true;
      }
      
      // Check for specialty keywords
      else {
        let foundSpecialty = false;
        for (const [specialty, keywords] of Object.entries(specialtyKeywords)) {
          if (keywords.some(keyword => keyword === token || token.includes(keyword))) {
            parsed.specialties.push(specialty);
            foundSpecialty = true;
            processed = true;
            break;
          }
        }
      }
      
      // If not location or specialty, treat as name
      if (!processed) {
        parsed.names.push(token);
      }
    }

    return parsed;
  },

  /**
   * Check if input contains location patterns
   * @param {string} input - Input to check
   * @returns {boolean} Whether input appears to be a location
   */
  isLocationInName(input) {
    const inputLower = input.toLowerCase().trim();
    
    // Check for zip code patterns
    if (/^\d{5}(-\d{4})?$/.test(input.trim())) {
      return true;
    }
    
    // Check for state abbreviations
    if (/^[A-Z]{2}$/i.test(input.trim())) {
      return true;
    }
    
    // Check for city, state patterns
    if (input.includes(',')) {
      return true;
    }
    
    // Check for common US states (full names)
    const states = [
      'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut',
      'delaware', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
      'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan',
      'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire',
      'new jersey', 'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio',
      'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
      'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia',
      'wisconsin', 'wyoming'
    ];
    
    // Check if input contains state names
    if (states.some(state => inputLower.includes(state))) {
      return true;
    }
    
    // Check for common city patterns and keywords
    const locationKeywords = [
      'city', 'town', 'county', 'parish', 'beach', 'springs', 'hills', 'valley', 'park',
      'heights', 'falls', 'lake', 'river', 'bay', 'island', 'mountain', 'grove', 'field',
      'angeles', 'francisco', 'diego', 'antonio', 'jose', 'austin', 'houston', 'dallas',
      'chicago', 'phoenix', 'philadelphia', 'miami', 'atlanta', 'boston', 'seattle',
      'denver', 'detroit', 'baltimore', 'memphis', 'charlotte', 'portland', 'milwaukee',
      'vegas', 'sacramento', 'oakland', 'fresno', 'tucson', 'mesa', 'kansas', 'virginia',
      'omaha', 'cleveland', 'tulsa', 'honolulu', 'tampa', 'cincinnati', 'pittsburgh'
    ];
    
    return locationKeywords.some(keyword => inputLower.includes(keyword));
  },

  /**
   * Get available search specialties
   * @returns {Array} List of specialty options
   */
  getSpecialtyOptions() {
    return [
      { value: 'all', label: 'All Specialties' },
      { value: 'anesthesiology', label: 'Anesthesiology' },
      { value: 'cardiology', label: 'Cardiology' },
      { value: 'dermatology', label: 'Dermatology' },
      { value: 'emergency', label: 'Emergency Medicine' },
      { value: 'endocrinology', label: 'Endocrinology' },
      { value: 'family', label: 'Family Medicine' },
      { value: 'gastroenterology', label: 'Gastroenterology' },
      { value: 'infectious', label: 'Infectious Disease' },
      { value: 'internal', label: 'Internal Medicine' },
      { value: 'nephrology', label: 'Nephrology' },
      { value: 'neurology', label: 'Neurology' },
      { value: 'obstetrics', label: 'Obstetrics & Gynecology' },
      { value: 'oncology', label: 'Oncology' },
      { value: 'ophthalmology', label: 'Ophthalmology' },
      { value: 'orthopedics', label: 'Orthopedics' },
      { value: 'otolaryngology', label: 'Otolaryngology (ENT)' },
      { value: 'pathology', label: 'Pathology' },
      { value: 'pediatrics', label: 'Pediatrics' },
      { value: 'plastic', label: 'Plastic Surgery' },
      { value: 'psychiatry', label: 'Psychiatry' },
      { value: 'pulmonology', label: 'Pulmonology' },
      { value: 'radiology', label: 'Radiology' },
      { value: 'rheumatology', label: 'Rheumatology' },
      { value: 'surgery', label: 'Surgery' },
      { value: 'urology', label: 'Urology' }
    ];
  }
};

export default NPIAPI;